/* eslint-disable react-hooks/exhaustive-deps */
import { ThemeProvider } from "@emotion/react";
import {
  Box,
  Grid,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import DefaultPaper from "../../components/DefaultPaper";
import ConfirmerForm from "../../components/Form/ConfirmerForm";
import TextFieldWithTooltip from "../../components/Input/TextFieldWithTooltip";
import ResponeseComponent from "../../components/ReponseComponent";
import AddEditDialogTable from "../../components/Table/AddEditDialogTable";
import useApi, { httpClient } from "../../hooks/useApi";
import { messageActions, toolBarMessageActions } from "../../store/store";
import { infoTheme } from "../../style/theme";
import getConfirmerListHead, {
  confirmerDataMapper,
} from "../../utils/ConfirmerListHead";
import Constants from "../../utils/Constants";
import MESSAGES from "../../utils/Messages";

// バリデーション定義
const schemaRequest = z.object({
  approvalRequestComment: z
    .string()
    .max(
      Constants.MULTI_LINE_TEXT_MAX,
      MESSAGES.TEXT_MAX_ERROR(Constants.MULTI_LINE_TEXT_MAX)
    ),
});
const schemaRemand = z.object({
  remandComment: z
    .string()
    .max(
      Constants.MULTI_LINE_TEXT_MAX,
      MESSAGES.TEXT_MAX_ERROR(Constants.MULTI_LINE_TEXT_MAX)
    ),
});
const ConfirmationLetter = ({ title }) => {
  const params = useParams();
  const { connect } = useApi();
  const [disableEdit, setDisableEdit] = useState(true);
  const [updateConfirmerList, setUpdateConfirmerList] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const refleshConfirmerList = () => {
    setUpdateConfirmerList(updateConfirmerList + 1);
  };
  // 確認状情報取得
  const [fetchResult, setFetchResult] = useState();
  const fetchData = async () => {
    const result = await connect(() => {
      return httpClient.get(`/api/confirmer/GetConfirmation?id=${params.id}`);
    });
    if (result?.status !== 200) {
      dispatch(messageActions.setError(MESSAGES.CONFIRMATION_LETTER_NOT_FOUND));
      navigate("/confirmer");
    }
    setFetchResult(result?.data);
    refleshConfirmerList();
  };
  useEffect(() => {
    fetchData();
  }, []);
  // 表示制御
  const initializeState = () => {
    const confirmationLetterStatus =
      fetchResult?.data?.confirmationLetterStatus;
    const authority = fetchResult?.data?.confirmerAuthority;

    let disableEdit =
      confirmationLetterStatus !==
        Constants.CONFIRMATION_LETTER_STATUS.SHIPPED &&
      confirmationLetterStatus !==
        Constants.CONFIRMATION_LETTER_STATUS
          .APPROVAL_REQUEST_REMANDED_BY_CONFIRMER;
    setDisableEdit(disableEdit);
    setDisableConfirmerList(disableEdit);
    // ツールバー設定
    if (
      confirmationLetterStatus === Constants.CONFIRMATION_LETTER_STATUS.SHIPPED
    ) {
      // 「送付済み」
      dispatch(toolBarMessageActions.setInfo(MESSAGES.RESPONSE_WAITING));
    } else if (
      confirmationLetterStatus ===
      Constants.CONFIRMATION_LETTER_STATUS
        .APPROVAL_REQUEST_REMANDED_BY_CONFIRMER
    ) {
      // 「差し戻し」
      dispatch(toolBarMessageActions.setWarning(MESSAGES.REMANDED));
    } else if (
      confirmationLetterStatus ===
      Constants.CONFIRMATION_LETTER_STATUS.WAITING_FOR_APPROVAL_OF_CONFIRMER
    ) {
      // 「確認先内での承認待ち」の場合メッセージ設定
      if (authority === Constants.CONFIRMER_AUTHORITY_TYPE.APPROVER) {
        dispatch(toolBarMessageActions.setInfo(MESSAGES.APPROVAL_REQUESTED));
      } else {
        dispatch(toolBarMessageActions.setInfo(MESSAGES.WAITING_FOR_APPROVAL));
      }
    } else if (
      confirmationLetterStatus ===
      Constants.CONFIRMATION_LETTER_STATUS.RESPONSED
    ) {
      // 「回収済み」の場合メッセージ設定
      dispatch(toolBarMessageActions.setSuccess(MESSAGES.RESPONSE_COMPLETED));
    }

    // ステッパーの状態設定
    let stepNum = -1;
    switch (confirmationLetterStatus) {
      case Constants.CONFIRMATION_LETTER_STATUS.SHIPPED:
      case Constants.CONFIRMATION_LETTER_STATUS
        .APPROVAL_REQUEST_REMANDED_BY_CONFIRMER:
        stepNum = 0;
        break;
      case Constants.CONFIRMATION_LETTER_STATUS
        .WAITING_FOR_APPROVAL_OF_CONFIRMER:
        stepNum = 1;
        break;
      case Constants.CONFIRMATION_LETTER_STATUS.RESPONSED:
        stepNum = 2;
        break;
      default:
        break;
    }
    setActiveStep(stepNum);
  };
  useEffect(() => initializeState(), [params, fetchResult]);

  // 確認依頼文関連
  const [requestStatementReplaced, setRequestStatementReplaced] = useState();
  const [confirmerList, setConfirmerList] = useState();
  useEffect(() => {
    let atena = fetchResult?.data?.confirmationDestination + "\n";
    atena =
      atena +
      confirmerList
        ?.filter((row) => row.isCreatedByAuditorOrClient)
        ?.map(
          (row) =>
            (row.department ? row.department + " " : "") + row.name + " 様"
        )
        .join("\n");
    let tempStatement = fetchResult?.data?.requestStatement?.replace(
      /#{宛名}/g,
      atena
    );
    tempStatement = tempStatement?.replace(
      /#{基準日}/g,
      fetchResult?.data?.referenceDateFormatted
    );
    tempStatement = tempStatement?.replace(
      /#{被監査企業}/g,
      fetchResult?.data?.companyName
    );
    setRequestStatementReplaced(tempStatement);
  }, [fetchResult, confirmerList]);

  // 担当者一覧関連
  const [disableConfirmerList, setDisableConfirmerList] = useState(true);

  // ステッパー関連
  const steps = ["回答入力", "承認", "回答送信"];
  const [activeStep, setActiveStep] = useState();
  return (
    <>
      {fetchResult?.data?.remandComment &&
        fetchResult?.data?.confirmationLetterStatus ===
          Constants.CONFIRMATION_LETTER_STATUS
            .APPROVAL_REQUEST_REMANDED_BY_CONFIRMER && (
          <Box
            sx={{
              position: "sticky",
              top: "3.5rem",
              zIndex: 100,
              backgroundColor: "white",
              paddingBottom: "0.5rem",
            }}
          >
            <TextField
              multiline
              label="差し戻しコメント"
              value={fetchResult?.data?.remandComment}
              disabled
              minRows={2}
              maxRows={6}
              fullWidth
              sx={{ marginTop: "0.5rem" }}
            />
          </Box>
        )}
      <Typography variant="h6">{title}</Typography>
      <Grid container sx={{ margin: "0.5rem" }}>
        <Grid xs={1} />
        <Grid xs={10}>
          <Box
            sx={{
              width: "100%",
            }}
          >
            <Stepper activeStep={activeStep}>
              {/* TODO:承認履歴の表示、承認をスキップした場合の処理 */}
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Grid>
        <Grid xs={1} />
      </Grid>
      <Typography variant="subtitle1" sx={{ whiteSpace: "pre-wrap" }}>
        {requestStatementReplaced}
      </Typography>

      {/* メニュー */}
      <DefaultPaper title={"担当者一覧"}>
        <AddEditDialogTable
          getPath={`/api/confirmer/GetConfirmerList?confirmationLetterId=${params.id}`}
          deletePath="/api/confirmer/DeleteConfirmer"
          headCells={getConfirmerListHead()}
          rowMapper={confirmerDataMapper}
          listName={"確認担当者"}
          editDialogContent={ConfirmerForm}
          dialogProps={{ confirmationLetterId: params.id }}
          disablePagination
          disableAdd={disableConfirmerList}
          disableDelete={disableConfirmerList}
          disableEdit={disableConfirmerList}
          _setRows={setConfirmerList}
          forceUpdate={updateConfirmerList}
          postSubmitHandler={fetchData}
        ></AddEditDialogTable>
      </DefaultPaper>

      {/* 概要 */}
      <DefaultPaper title="確認状概要">
        <ThemeProvider theme={infoTheme}>
          <Grid container>
            <Grid item xs={3}>
              <TextFieldWithTooltip
                label="被監査企業"
                value={fetchResult?.data?.companyName}
              ></TextFieldWithTooltip>
            </Grid>
            <Grid item xs={9} display="flex" alignItems="flex-end">
              <TextFieldWithTooltip
                label="回収希望日"
                value={fetchResult?.data?.desiredCollectionDateFormatted}
              />
              <Typography>までにご回答をお願い致します。</Typography>
            </Grid>
          </Grid>
        </ThemeProvider>
      </DefaultPaper>

      {/* 確認内容 */}
      <DefaultPaper title={"確認内容"}>
        <ResponeseComponent
          targetData={fetchResult?.data}
          presenceOrAbsenceList={fetchResult?.presenceOrAbsenceList}
          postSaveHandler={fetchData}
          isApproved={fetchResult?.data?.isApproved}
          readOnly={disableEdit}
        />
      </DefaultPaper>
    </>
  );
};

export default ConfirmationLetter;
